/**
 * Transcripción descargable: texto plano (con marcas de tiempo y hablante) y
 * subtítulos SRT / WebVTT para montar sobre la copia local o un video.
 */
import type { TranscriptSegment } from '@eco/shared'

/** Un subtítulo no debería pasar de ~2 líneas de 42 caracteres. */
const MAX_CUE_CHARS = 84

const pad = (n: number, w = 2) => String(n).padStart(w, '0')

function clock(ms: number, sep: ',' | '.'): string {
  const t = Math.max(0, Math.round(ms))
  const h = Math.floor(t / 3_600_000)
  const m = Math.floor((t % 3_600_000) / 60_000)
  const s = Math.floor((t % 60_000) / 1000)
  return `${pad(h)}:${pad(m)}:${pad(s)}${sep}${pad(t % 1000, 3)}`
}

function mmss(ms: number): string {
  const t = Math.floor(Math.max(0, ms) / 1000)
  const h = Math.floor(t / 3600)
  const m = Math.floor((t % 3600) / 60)
  return h ? `${h}:${pad(m)}:${pad(t % 60)}` : `${pad(m)}:${pad(t % 60)}`
}

/** `[mm:ss] Hablante: texto`, fusionando turnos seguidos del mismo hablante. */
export function transcriptTxt(titulo: string, segments: TranscriptSegment[]): string {
  const lines: string[] = [titulo, '']
  let cur: { inicioMs: number; speaker: string; texto: string } | null = null
  for (const s of segments) {
    const texto = s.texto.trim()
    if (!texto) continue
    if (cur && cur.speaker === s.speaker) {
      cur.texto += ` ${texto}`
      continue
    }
    if (cur) lines.push(`[${mmss(cur.inicioMs)}] ${cur.speaker}: ${cur.texto}`)
    cur = { inicioMs: s.inicioMs, speaker: s.speaker, texto }
  }
  if (cur) lines.push(`[${mmss(cur.inicioMs)}] ${cur.speaker}: ${cur.texto}`)
  return lines.join('\n') + '\n'
}

/**
 * Divide un segmento largo en subtítulos de hasta MAX_CUE_CHARS por límite de
 * palabra, repartiendo el tiempo en proporción al texto.
 */
export function cues(
  segments: TranscriptSegment[]
): { inicioMs: number; finMs: number; texto: string }[] {
  const out: { inicioMs: number; finMs: number; texto: string }[] = []
  for (const s of segments) {
    const texto = `${s.speaker}: ${s.texto.trim()}`
    if (!s.texto.trim()) continue
    const partes: string[] = []
    let cur = ''
    for (const w of texto.split(/\s+/)) {
      if (cur && (cur + ' ' + w).length > MAX_CUE_CHARS) {
        partes.push(cur)
        cur = w
      } else cur = cur ? `${cur} ${w}` : w
    }
    if (cur) partes.push(cur)
    const total = partes.reduce((n, p) => n + p.length, 0)
    const dur = Math.max(1, s.finMs - s.inicioMs)
    let t = s.inicioMs
    partes.forEach((p, i) => {
      const fin = i === partes.length - 1 ? s.finMs : Math.round(t + (dur * p.length) / total)
      out.push({ inicioMs: t, finMs: Math.max(fin, t + 1), texto: p })
      t = fin
    })
  }
  return out
}

export function transcriptSrt(segments: TranscriptSegment[]): string {
  return (
    cues(segments)
      .map((c, i) => `${i + 1}\n${clock(c.inicioMs, ',')} --> ${clock(c.finMs, ',')}\n${c.texto}\n`)
      .join('\n') + '\n'
  )
}

export function transcriptVtt(segments: TranscriptSegment[]): string {
  return (
    'WEBVTT\n\n' +
    cues(segments)
      .map((c) => `${clock(c.inicioMs, '.')} --> ${clock(c.finMs, '.')}\n${c.texto}\n`)
      .join('\n')
  )
}
